import { useEffect } from 'react';
import { DEFAULT_META_DESCRIPTION } from '../../constants/seoDefaults';
import { getSiteOrigin } from '../../utils/siteOrigin';

type PageMetaProps = {
  title: string;
  description: string;
  /** Path only, e.g. `/blog` or `/blog/my-post` */
  canonicalPath: string;
  ogType?: 'website' | 'article';
  articlePublishedTime?: string;
  keywords?: string;
  /** Absolute URL for Open Graph / Twitter cards */
  ogImage?: string;
  /** Sets document.documentElement.lang while mounted */
  htmlLang?: string;
  /** Alternate language URLs for hreflang */
  hreflangAlternates?: { hreflang: string; href: string }[];
};

/**
 * Updates document title and core SEO / Open Graph tags for SPA routes.
 * Restores prior head state on unmount.
 */
function PageMeta({
  title,
  description,
  canonicalPath,
  ogType = 'website',
  articlePublishedTime,
  keywords,
  ogImage,
  htmlLang,
  hreflangAlternates,
}: PageMetaProps) {
  useEffect(() => {
    const origin = getSiteOrigin().replace(/\/$/, '');
    const path = canonicalPath.startsWith('/')
      ? canonicalPath
      : `/${canonicalPath}`;
    const canonicalUrl = `${origin}${path}`;

    const prevTitle = document.title;
    document.title = title;

    const prevHtmlLang = document.documentElement.lang;
    if (htmlLang) {
      document.documentElement.lang = htmlLang;
    }

    const existingDesc = document.querySelector(
      'meta[name="description"]'
    ) as HTMLMetaElement | null;
    const prevDesc = existingDesc?.getAttribute('content') ?? null;
    let createdDesc: HTMLMetaElement | null = null;
    if (existingDesc) {
      existingDesc.setAttribute('content', description);
    } else {
      createdDesc = document.createElement('meta');
      createdDesc.setAttribute('name', 'description');
      createdDesc.setAttribute('content', description);
      document.head.appendChild(createdDesc);
    }

    const existingCanon = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;
    const prevCanonHref = existingCanon?.getAttribute('href') ?? null;
    let createdCanon: HTMLLinkElement | null = null;
    if (existingCanon) {
      existingCanon.setAttribute('href', canonicalUrl);
    } else {
      createdCanon = document.createElement('link');
      createdCanon.rel = 'canonical';
      createdCanon.setAttribute('href', canonicalUrl);
      document.head.appendChild(createdCanon);
    }

    const hreflangLinks: HTMLLinkElement[] = [];
    if (hreflangAlternates?.length) {
      document
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((el) => el.remove());
      for (const alt of hreflangAlternates) {
        const link = document.createElement('link');
        link.rel = 'alternate';
        link.setAttribute('hreflang', alt.hreflang);
        link.href = alt.href;
        document.head.appendChild(link);
        hreflangLinks.push(link);
      }
    }

    const ogMetas: HTMLMetaElement[] = [];
    const pushMeta = (attr: 'name' | 'property', key: string, value: string) => {
      const el = document.createElement('meta');
      el.setAttribute(attr, key);
      el.setAttribute('content', value);
      document.head.appendChild(el);
      ogMetas.push(el);
    };
    pushMeta('property', 'og:title', title);
    pushMeta('property', 'og:description', description);
    pushMeta('property', 'og:url', canonicalUrl);
    pushMeta('property', 'og:type', ogType);
    if (htmlLang) {
      pushMeta('property', 'og:locale', htmlLang.replace('-', '_'));
    }
    pushMeta('name', 'twitter:card', 'summary_large_image');
    pushMeta('name', 'twitter:title', title);
    pushMeta('name', 'twitter:description', description);
    if (keywords?.trim()) {
      pushMeta('name', 'keywords', keywords.trim());
    }
    if (ogType === 'article' && articlePublishedTime) {
      pushMeta('property', 'article:published_time', articlePublishedTime);
    }
    if (ogImage?.trim()) {
      const url = ogImage.trim();
      pushMeta('property', 'og:image', url);
      pushMeta('name', 'twitter:image', url);
    }

    return () => {
      document.title = prevTitle;
      if (htmlLang) {
        document.documentElement.lang = prevHtmlLang || 'en';
      }
      if (existingDesc && prevDesc !== null) {
        existingDesc.setAttribute('content', prevDesc);
      } else if (existingDesc) {
        existingDesc.setAttribute('content', DEFAULT_META_DESCRIPTION);
      }
      if (createdDesc?.parentNode) {
        createdDesc.remove();
      }
      if (createdCanon?.parentNode) {
        createdCanon.remove();
      } else if (existingCanon && prevCanonHref !== null) {
        existingCanon.setAttribute('href', prevCanonHref);
      }
      hreflangLinks.forEach((el) => el.remove());
      ogMetas.forEach((el) => el.remove());
    };
  }, [
    title,
    description,
    canonicalPath,
    ogType,
    articlePublishedTime,
    keywords,
    ogImage,
    htmlLang,
    // Stable compare for alternate list
    JSON.stringify(hreflangAlternates ?? []),
  ]);

  return null;
}

export default PageMeta;
